import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { TodoListResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UpdateTodoListInput {
  name?: string;
  description?: string | null;
  isPublic?: boolean;
  publicSlug?: string | null;
}

interface UseTodoListReturn {
  list: TodoListResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  update: (input: UpdateTodoListInput) => Promise<TodoListResponse>;
  archive: () => Promise<TodoListResponse>;
  restore: () => Promise<TodoListResponse>;
  remove: () => Promise<void>;
}

/**
 * Holder-scoped todo-list detail. Returns just the list row — sections
 * + items are independently queried (useTodoSections, useTodoItems) so
 * a section/item mutation only invalidates the slice it touches.
 */
export function useTodoList(holderId: string | null, listId: string): UseTodoListReturn {
  const queryClient = useQueryClient();
  const detailKey = queryKeys.todoLists.detail(holderId ?? '', listId);
  const myKey = queryKeys.todoLists.my(holderId ?? '');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: detailKey,
    queryFn: () => api.getTodoList(holderId as string, listId),
    enabled: !!holderId && !!listId,
  });

  const updateMut = useMutation({
    mutationFn: (input: UpdateTodoListInput) =>
      api.updateTodoList(holderId as string, listId, input).then((r) => r.list),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
      queryClient.invalidateQueries({ queryKey: myKey });
    },
  });

  const archiveMut = useMutation({
    mutationFn: () => api.archiveTodoList(holderId as string, listId).then((r) => r.list),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
      queryClient.invalidateQueries({ queryKey: myKey });
    },
  });

  const restoreMut = useMutation({
    mutationFn: () => api.restoreTodoList(holderId as string, listId).then((r) => r.list),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
      queryClient.invalidateQueries({ queryKey: myKey });
    },
  });

  const removeMut = useMutation({
    mutationFn: () => api.deleteTodoList(holderId as string, listId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: myKey });
    },
  });

  return {
    list: data?.list ?? null,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load todo list' : null,
    refresh: () => {
      refetch();
    },
    update: (input) => updateMut.mutateAsync(input),
    archive: () => archiveMut.mutateAsync(),
    restore: () => restoreMut.mutateAsync(),
    remove: () => removeMut.mutateAsync(),
  };
}
